import SText from '../../../components/opinionless/SText'
import SButton from '../../../components/opinionless/SButton'
import React, { useState } from 'react'
import { SStyled, useSTheme } from '../../../styles/theme'
import { useDocumentUploadMutation } from '../../../types/graphql'
import ConditionalRender from '../../../utils/conditionalRender'
import SLoadingIndicator from '../../../components/opinionless/SLoadingIndicator'
import { useTranslation } from '../../../i18n'
import SBox from '../../../components/opinionless/SBox'
import { ga4_inputChanged, ga4_useTitle } from 'utils/ga4'

interface Props {
    setCurrentStep?: (number) => void
    applicationId: string
    fontFamily: any
}

const DOCUMENT_TYPE = {
    DRIVERS_LICENSE: { en: `driver's license`, es: 'licencia de conducir' },
    ID_CARD: { en: 'ID Card', es: 'tarjeta de identificación' },
}

export const Container = SStyled(SBox)`
    width: 100%;
    max-width: 600px;
`

const ApplicationStep3: React.FC<Props> = (props: Props) => {
    const theme = useSTheme()
    const [uploadDocMutation] = useDocumentUploadMutation()
    const [fileToBeUploadedList, setFileToBeUploadedList] = useState({})
    const [fileUploadPending, setFileUploadPending] = useState('')
    const { t } = useTranslation(window.LANG_NS)

    const fontFamily = props.fontFamily

    ga4_useTitle('Step 3')

    if (!window.DOCUMENT_REQ.length) {
        props.setCurrentStep(4)
    }

    const handleUpload = (e) => {
        const documentType = e.target.getAttribute('data-type')
        if (e?.target?.files?.[0]) {
            if (e?.target?.files?.[0].size < 5097152) {
                setFileUploadPending(documentType)
                uploadDocMutation({
                    variables: {
                        file: e.target.files[0],
                        personId: props.applicationId,
                        documentType: documentType,
                    },
                })
                    .then(() => {
                        const filesToBeUploaded = JSON.parse(JSON.stringify(fileToBeUploadedList))
                        filesToBeUploaded[documentType] = true
                        setFileToBeUploadedList(filesToBeUploaded)
                    })
                    .catch((error) => {
                        console.error(error)
                    })
                    .finally(() => {
                        setFileUploadPending('')
                    })

                e.target.value = ''
            } else {
                alert('Size of the file is too big')
            }
        }
    }

    return (
        <Container>
            <SText
                textAlign={'left'}
                fontWeight={'bold'}
                fontSize={'32px'}
                mb={5}
                color="black"
                fontFamily={fontFamily.header}
            >
                Upload Documentation
            </SText>
            {window.DOCUMENT_REQ.map((docreq, idx) => {
                let isRequired = ''
                if (docreq.required) {
                    isRequired = '\u002A'
                }
                return (
                    <div key={idx}>
                        <SText
                            fontSize={'20px'}
                            textAlign={'left'}
                            color="gray400"
                            mb={7}
                            mt={3}
                            sx={{ whiteSpace: 'pre-line' }}
                            fontFamily={fontFamily.body}
                        >
                            {t('upload_documentation')}
                            {` ${DOCUMENT_TYPE[docreq.type][window.S18N.language]}`}
                            {` ${isRequired}`}
                        </SText>

                        <input
                            type="file"
                            id={`${docreq.type}-file-upload`}
                            data-type={docreq.type}
                            disabled={!!fileUploadPending}
                            accept=".doc, .docx, .pdf, .png, .jpg, .heic"
                            onChange={(e) => {
                                handleUpload?.(e)
                                ga4_inputChanged(`${docreq.type}-file-upload`, true)
                            }}
                            style={{ display: 'none' }}
                        />
                        <div
                            style={{
                                width: '100%',
                                height: '300px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: 'lightgray',
                                borderRadius: '20px',
                                marginBottom: '20px',
                            }}
                        >
                            <label
                                htmlFor={`${docreq.type}-file-upload`}
                                style={{ fontSize: '24px', fontFamily: fontFamily.body }}
                            >
                                <ConditionalRender if={fileUploadPending === docreq.type}>
                                    <SLoadingIndicator />
                                </ConditionalRender>
                                <ConditionalRender if={fileUploadPending !== docreq.type}>
                                    {fileToBeUploadedList[docreq.type] ? 'File Uploaded!' : 'Click to upload file'}
                                </ConditionalRender>
                            </label>
                        </div>
                    </div>
                )
            })}
            <SButton
                onClick={() => props.setCurrentStep(4)}
                title={t('continue')}
                width={'100%'}
                mt={20}
                textStyle={{ fontFamily: fontFamily.body }}
                ga4_key={t('continue', { lang: 'en' })}
            >
                {t('continue')}
            </SButton>
            <SButton
                onClick={() => {}}
                title={t('application_alternative_text')}
                width={'100%'}
                mt={20}
                backgroundColor={'grey'}
                textStyle={{ fontFamily: fontFamily.body }}
                ga4_key={t('application_alternative_text', { lang: 'en' })}
            >
                {t('application_alternative_text')}
            </SButton>
        </Container>
    )
}

export default ApplicationStep3
